import { Injectable } from '@angular/core';
import { catchError, Observable, tap, throwError } from 'rxjs';
import { formatUrl } from '../shared/utils/format-url-util';
import { ENDPOINTS } from '../shared/constants/url.constants';
import { HttpRequestService } from '../shared/services/http-request.service';
import { GlobalErrorHandlerService } from '../shared/services/global-error-handler.service';
import { NotificationService } from '../shared/services/notification.service';
import { HttpClient, HttpHeaders } from '@angular/common/http';

@Injectable({
  providedIn: 'root',
})
export class InvoiceService {
  constructor(
    private httpClient: HttpRequestService,
    private http: HttpClient,
    private errorHandler: GlobalErrorHandlerService,
    private notificationService: NotificationService
  ) { }

  saveInvoice(payload: any, programCode: string): Observable<any> {
    const url = formatUrl(ENDPOINTS.INVOICE.SAVE_INVOICE, { program_code: programCode });
    return this.httpClient
      .post(url, payload, true)
      .pipe(
        tap(() => {
          this.notificationService.success(
            'Success',
            `Invoice submitted successfully.`
          );
        }),
        catchError((err) => {
          return throwError(() => this.errorHandler.handleError(err));
        })
      )
  }

  getInvoice(invoiceId: number, programCode: string): Observable<any> {
    const url = formatUrl(ENDPOINTS.INVOICE.GET_INVOICE, {
      program_code: programCode,
      invoice_id: invoiceId,
    });

    return this.httpClient.get(url, true).pipe(
      catchError((err) => {
        return throwError(() => this.errorHandler.handleError(err));
      })
    );
  }

  updateInvoiceLinesStatus(payload: any, programCode: string): Observable<any> {
    const url = formatUrl(ENDPOINTS.INVOICE.UPDATE_INVOICE_LINES_STATUS, { program_code: programCode });
    return this.httpClient
      .post(url, payload, true)
      .pipe(
        tap(() => {
          this.notificationService.success(
            'Success',
            `Invoice ${payload?.action === 'Approve' ? 'approved' : payload?.action === 'Reject' ? 'denied' : 'saved'} successfully.`
          );
        }),
        catchError((err) => {
          return throwError(() => this.errorHandler.handleError(err));
        })
      )
  }

  /**
   * Deny invoice lines. Payload: { invoiceLineIds: number[], remarks: string, status: 'D' }.
   * Used by the Good To Pay Invoices list for both Deny and Deny All.
   */
  denyInvoice(payload: any, programCode: string): Observable<any> {
    const url = formatUrl(ENDPOINTS.INVOICE.DENY_INVOICE, { program_code: programCode });
    return this.httpClient
      .post(url, payload, true)
      .pipe(
        tap(() => {
          this.notificationService.success('Success', 'Invoice denied successfully.');
        }),
        catchError((err) => {
          return throwError(() => this.errorHandler.handleError(err));
        })
      );
  }

  saveAdjudicationDetails(payload: any, programCode: string): Observable<any> {
    const url = formatUrl(ENDPOINTS.INVOICE.SAVE_ADJUDICATIONS, { program_code: programCode });
    return this.httpClient
      .post(url, payload, false)
      .pipe(
        tap(() => {
          this.notificationService.success(
            'Success',
            `Adjudication saved successfully.`
          );
        }),
        catchError((err) => {
          return throwError(() => this.errorHandler.handleError(err));
        })
      )
  }

  /** clmapi list pages, e.g. pendManagementInvoiceList and pendCodesInvoice. */
  getListPageData(payload: any): Observable<any> {
    return this.httpClient
      .post(ENDPOINTS.LIST_PAGE_DATA, payload)
      .pipe(
        catchError((err) => {
          return throwError(() => this.errorHandler.handleError(err));
        })
      );
  }

  /** Pend management Override / Override All / Deny / Deny All for invoice lines. */
  pendManagementAction(endpointUrl: string, programCode: string, payload: any, placeholder: string): Observable<any> {
    const url = formatUrl(endpointUrl, { program_code: programCode });
    return this.httpClient
      .post(url, payload, true)
      .pipe(
        tap(() => {
          this.notificationService.success('Success', `Invoice ${placeholder} successfully.`);
        }),
        catchError((err) => {
          return throwError(() => this.errorHandler.handleError(err));
        })
      );
  }

  submitAdjudicationDetails(payload: any, programCode: string): Observable<any> {
    const url = formatUrl(ENDPOINTS.INVOICE.SUBMIT_ADJUDICATION, { program_code: programCode });
    return this.httpClient
      .post(url, payload, false)
      .pipe(
        tap(() => {
          this.notificationService.success(
            'Success',
            `Adjudication submitted successfully.`
          );
        }),
        catchError((err) => {
          return throwError(() => this.errorHandler.handleError(err));
        })
      )
  }

  postMoveBackLineItemsStatus(payload: any, programCode: string): Observable<any> {
    const url = formatUrl(ENDPOINTS.INVOICE.STATUS_MOVE_BACK, { program_code: programCode });
    return this.httpClient
      .post(url, payload, false)
      .pipe(
        tap(() => {
          this.notificationService.success(
            'Success',
            `Updated Invoice Line Items status successfully.`
          );
        }),
        catchError((err) => {
          return throwError(() => this.errorHandler.handleError(err));
        })
      )
  }

  postInactivateLines(payload: any, programCode: string): Observable<any> {
    const url = formatUrl(ENDPOINTS.INVOICE.INACTIVATE_LINE, { program_code: programCode });
    return this.httpClient
      .post(url, payload, false)
      .pipe(
        tap(() => {
          this.notificationService.success(
            'Success',
            `Inactivated Invoice Line Items status successfully.`
          );
        }),
        catchError((err) => {
          return throwError(() => this.errorHandler.handleError(err));
        })
      )
  }

  getAuthUsageData(programCode: string, invoiceLineId: number){
    const url = formatUrl(ENDPOINTS.INVOICE.GETAUTH_USAGE, { program_code: programCode, invoiceLineId: invoiceLineId });
    return this.httpClient.get(url, true).pipe(
      catchError((err) => {
        return throwError(() => this.errorHandler.handleError(err));
      })
    );
  }

  getAuthInvoiceUtilization(programCode: string, authId: string, participantId: string): Observable<any> {
    const url = formatUrl(ENDPOINTS.INVOICE.GET_AUTH_INVOICE_UTILIZATION, {
      programCode, participantId, authId
    });

    return this.httpClient.get(url).pipe(
      catchError((err) => {
        return throwError(() => this.errorHandler.handleError(err));
      })
    );
  }

  adjustInvoceRequest(endpointUrl: string, programCode: string, payload: any, placeholder: string): Observable<any> {
    const url = formatUrl(endpointUrl, { programCode });

    return this.httpClient
      .post(url, payload, true)
      .pipe(
        tap(() => {
          this.notificationService.success(
            'Success',
            `${placeholder} successfully.`
          );
        }),
        catchError((err) => {
          return throwError(() => this.errorHandler.handleError(err));
        })
      )
  }

  importInvoice(file: FormData, programCode: string): Observable<any> {
    const url = formatUrl(ENDPOINTS.INVOICE.IMPORT_INVOICE, {
      programCode
    });

    return this.httpClient.post(url, file).pipe(
      tap((res: any) => {
        this.notificationService.success(
          res.responsemsg[0].desc,
          res.responsedata.message
        );
      }),
      catchError((err) => {
        return throwError(() => this.errorHandler.handleError(err));
      })
    );
  }
}
